import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * Adds focus safety, reduced-motion support and editor selection to the native slideshow.
 * @typedef {Object} Refs
 * @property {HTMLElement} [status]
 * @extends {Component<Refs>}
 */
class EsotericaeSlideshow extends Component {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {MutationObserver | undefined} */
  #observer;
  #motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #pointerActive = false;
  /** @type {Animation | undefined} */
  #progress;
  /** @type {HTMLElement | undefined} */
  #progressTarget;
  #progressDuration = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#slideshow?.suspend();
    this.#pointerActive = false;
    this.#resetProgress();
  }

  get #slideshow() {
    return /** @type {import('./slideshow.js').Slideshow | null} */ (this.querySelector('slideshow-component'));
  }

  async #setup() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#resetProgress();
    this.#pointerActive = false;
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    await customElements.whenDefined('slideshow-component');
    if (signal.aborted || !this.isConnected) return;
    const slideshow = this.#slideshow;
    if (!slideshow) return;

    this.#sync();
    this.#resumePlayback();
    this.addEventListener('mouseenter', this.#pausePlayback, { signal });
    this.addEventListener('mouseleave', this.#resumePlayback, { signal });
    this.addEventListener('focusin', this.#resumePlayback, { signal });
    this.addEventListener('focusout', () => {
      queueMicrotask(() => {
        if (!signal.aborted) this.#resumePlayback();
      });
    }, { signal });
    this.addEventListener('pointerdown', () => {
      this.#pointerActive = true;
      this.#pausePlayback();
    }, { signal, passive: true });
    const releasePointer = () => {
      if (!this.#pointerActive) return;
      this.#pointerActive = false;
      this.#resumePlayback();
    };
    document.addEventListener('pointerup', releasePointer, { signal });
    document.addEventListener('pointercancel', releasePointer, { signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.#pointerActive = false;
      this.#resumePlayback();
    }, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#onSelect, { signal });
    this.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    this.#motion.addEventListener('change', () => {
      this.#syncProgress();
      this.#resumePlayback();
    }, { signal });
    this.#observer = new MutationObserver(() => this.#sync());
    this.#observer.observe(slideshow, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'aria-selected', 'paused'],
    });
  }

  #pausePlayback = () => {
    this.#slideshow?.pause();
    this.#progress?.pause();
  };

  // The native timer uses the progress animation's remaining time after a pause.
  #resumePlayback = () => {
    const slideshow = this.#slideshow;
    if (!slideshow || !this.isConnected) return;
    this.#pausePlayback();
    if (!slideshow.autoplay || (slideshow.slides?.length || 0) < 2 || this.#motion.matches ||
      Shopify.designMode || document.hidden || this.matches(':hover') || this.#pointerActive ||
      this.querySelector(':focus-visible')) return;
    const elapsed = Number(this.#progress?.currentTime) || 0;
    slideshow.play(Math.max(1, (slideshow.autoplayInterval || 0) - elapsed));
    this.#progress?.play();
  };

  #resetProgress() {
    this.#progress?.cancel();
    this.#progress = undefined;
    this.#progressTarget = undefined;
    this.#progressDuration = 0;
    this.removeAttribute('data-progress');
  }

  #syncProgress() {
    const slideshow = this.#slideshow;
    const target = slideshow?.querySelector('[ref="dots[]"][aria-selected="true"] .esotericae-slideshow__progress');
    const duration = slideshow?.autoplayInterval;
    if (!(target instanceof HTMLElement) || !duration || this.#motion.matches || Shopify.designMode) {
      this.#resetProgress();
      return;
    }
    if (target === this.#progressTarget && duration === this.#progressDuration) return;
    this.#resetProgress();
    this.#progressTarget = target;
    this.#progressDuration = duration;
    this.setAttribute('data-progress', '');
    this.#progress = target.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], {
      duration,
      fill: 'both',
      easing: 'linear',
    });
    this.#progress.pause();
    this.#resumePlayback();
  }

  #sync() {
    const slideshow = this.#slideshow;
    if (!slideshow) return;
    slideshow.setAttribute('aria-live', 'off');
    for (const slide of slideshow.querySelectorAll('slideshow-slides > slideshow-slide')) {
      if (slide instanceof HTMLElement) slide.inert = slide.getAttribute('aria-hidden') === 'true';
    }
    for (const dot of slideshow.querySelectorAll('[ref="dots[]"]')) {
      if (dot.getAttribute('aria-selected') === 'true') dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    }
    this.#syncProgress();
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    this.#pausePlayback();
    if (!(event.target instanceof Element) || !event.target.closest('slideshow-controls')) return;
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const slideshow = this.#slideshow;
    if (!slideshow) return;
    event.preventDefault();
    const rtl = getComputedStyle(this).direction === 'rtl';
    if (event.key === 'Home') slideshow.select(0, event);
    else if (event.key === 'End') slideshow.select((slideshow.slides?.length || 1) - 1, event);
    else if ((event.key === 'ArrowRight') !== rtl) slideshow.next(event);
    else slideshow.previous(event);
  };

  /** @param {Event} event */
  #onSelect = (event) => {
    this.#sync();
    if (!(event instanceof SlideshowSelectEvent) || !event.detail.userInitiated || !this.matches(':focus-within')) return;
    const label = event.detail.slide?.getAttribute('aria-label');
    if (label && this.refs.status) this.refs.status.textContent = label;
  };

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    const slide = event.target.closest('slideshow-slide');
    const slideshow = this.#slideshow;
    const index = slideshow?.slides?.indexOf(/** @type {HTMLElement} */ (slide));
    if (index === undefined || index < 0) return;
    this.#pausePlayback();
    slideshow?.select(index, undefined, { animate: false });
  };
}

if (!customElements.get('esotericae-slideshow')) {
  customElements.define('esotericae-slideshow', EsotericaeSlideshow);
}
